import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import getConfig from 'next/config';
import {
    getTranslation,
    getTranslationKey,
    useTranslation,
} from 'utils/localization';
import AppLayout from 'components/layouts/AppLayout';
import Table, {
    createDetailLinkTh,
    createTd,
    createTh,
} from 'components/tables/Table';
import {
    ColumnColor,
    ColumnHeadInterface,
    ColumnSize,
    ColumnValueInterface,
    ColumnValueWeight,
    CountryInterface,
    FiltersInterface,
} from 'components/interfaces/GeneralInterface';
import { ClubListItemInterface } from 'components/interfaces/ClubInterface';
import { getPersonFilters } from 'components/pages/person/list/base/MembersFilters';
import { PERSON_FILTER_KEYS } from 'constants/filterKeysConstants';
import { DisciplineEnum, TNamespaceEnum } from 'types/types';
import styles from 'components/pages/base/list/ListPage.module.scss';
import { PaginatorPageInterface } from 'components/interfaces/Paginator';
import { ConfigPageInterface } from 'utils/helpers/server/serverSideProps/config';
import {
    DisciplineStateType,
    MemberListPageApiInterface,
} from 'submodules/api_middleware';
import { Config_PersonList_ApiInterface } from 'submodules/api_middleware/src/interfaces/config/PersonListConfigApiInterface';
import { ActionMapInterface } from 'components/pages/base/topControlBlock/TopControlBlock';
import MergeModal from 'components/pages/person/list/base/MergeModal';
import { countryFromCode } from 'utils/helpers/browser/translate';
import { connectStrings } from 'utils/helpers/browser/string';
import { MOBILE_PHONE_MAX_WIDTH } from 'constants/general';

interface Props
    extends PaginatorPageInterface,
        ConfigPageInterface<Config_PersonList_ApiInterface> {
    members: MemberListPageApiInterface[];
    countriesList: CountryInterface[];
    clubList: ClubListItemInterface[];
    discipline: DisciplineEnum;
}

const MembersPage = ({
    members,
    paginator,
    countriesList,
    clubList,
    discipline,
    config,
}: Props): ReactElement => {
    const { t } = useTranslation();

    const [showMergeModal, setShowMergeModal] = useState<boolean>(false);

    const [isMobile, setIsMobile] = useState(false);
    const mediaQuery = useMediaQuery({ maxWidth: MOBILE_PHONE_MAX_WIDTH });

    useEffect(() => {
        if (mediaQuery !== isMobile) {
            setIsMobile(mediaQuery);
        }
    }, [mediaQuery]);

    const filterConfig: FiltersInterface = getPersonFilters(
        t,
        countriesList,
        clubList,
        isMobile
            ? 'Members.searchPlaceholderMobile'
            : 'Members.searchPlaceholder',
        [
            PERSON_FILTER_KEYS.birth_year,
            PERSON_FILTER_KEYS.age_from,
            PERSON_FILTER_KEYS.age_to,
            PERSON_FILTER_KEYS.gender,
            //PERSON_FILTER_KEYS.roles,
            // @ts-ignore-next-line
            PERSON_FILTER_KEYS[`${discipline}_state`],
            // PERSON_FILTER_KEYS.owner_club_internal_ids,
            PERSON_FILTER_KEYS.country_code,
            // @ts-ignore-next-line
            PERSON_FILTER_KEYS[`${discipline}_membership_unpaid`],
        ],
        undefined,
        discipline
    );

    const { publicRuntimeConfig } = getConfig();
    const is1Url = publicRuntimeConfig.NEXT_PUBLIC_IS1_URL;
    const columns: ColumnHeadInterface[] = useMemo<ColumnHeadInterface[]>(
        () => [
            createDetailLinkTh((external_id) =>
                external_id !== null
                    ? is1Url +
                      '/hraci/osoby-detail/?req=' +
                      external_id +
                      '&sport=fotbal'
                    : undefined
            ),
            createTh(t, getTranslationKey(TNamespaceEnum.person, 'idName')),
            createTh(
                t,
                getTranslationKey(TNamespaceEnum.person, 'countryBorn')
            ),
            createTh(
                t,
                getTranslationKey(TNamespaceEnum.person, 'nationalIdentifier')
            ),
            // createTh(t, 'PlayerProfile.registeredInClub', ColumnSize.L),
            createTh(
                t,
                'MemberProfile.membership',
                ColumnSize.S,
                undefined,
                true
            ),
            /*
            createTh(
                t,
                getTranslationKey(TNamespaceEnum.person, 'function'),
                undefined,
                undefined,
                true
            ),
            */
            createTh(
                t,
                getTranslationKey(TNamespaceEnum.person, 'gender'),
                ColumnSize.S,
                undefined,
                true
            ),
        ],
        []
    );
    const data: ColumnValueInterface[][] = members.map(
        // @ts-ignore-next-line
        (member) => {
            const memberDisciplineState =
                member[`${discipline}_state` as DisciplineStateType];

            return [
                createTd(member.external_id),
                createTd(
                    [
                        '' + member?.internal_id,
                        connectStrings(
                            ' ',
                            member.last_name,
                            member.first_name
                        ),
                    ],
                    ColumnValueWeight.BOLD_LAST
                ),
                createTd(
                    [countryFromCode(t, member.citizen_of), member.birth_year],
                    ColumnValueWeight.BOLD_LAST
                ),
                createTd(member.national_identifier),
                /*
                createTd(
                    createLinkByType(
                        t,
                        {
                            name: player[`${discipline}` as DisciplineEnum]
                                .registered.name,
                            id: player[`${discipline}` as DisciplineEnum]
                                .registered.id,
                        },
                        EntityReferenceApiEnum.club
                    )
                ),
                */
                createTd(
                    memberDisciplineState === 'active' ||
                        memberDisciplineState === 'active_next_year_deadline' ||
                        memberDisciplineState ===
                            'active_next_year_deadline_protection',
                    undefined,
                    // Note: ColumnColor affects the icon
                    memberDisciplineState === 'active_next_year_deadline' ||
                        memberDisciplineState === 'archive' ||
                        memberDisciplineState ===
                            'active_next_year_deadline_protection'
                        ? ColumnColor.GRAY
                        : undefined
                ),
                /*
                createTd(
                    member.roles.map((role: RoleApiEnum) => ({
                        name: role,
                        ...getLinkByRole(role, person.id, t),
                    }))
                ),
                 */
                createTd(
                    member.gender === null
                        ? ''
                        : getTranslation(
                              t,
                              TNamespaceEnum.person,
                              `${member.gender}-short`
                          ),
                    undefined
                ),
            ];
        }
    );

    const topActionsMap: ActionMapInterface = {
        // TODO IT-572
        /*['merge_person']: {
            localization: t('actions.personMerge'),
            cb: () => setShowMergeModal(true),
        },*/
    };

    return (
        <AppLayout
            pageTitle={{
                primaryBoldText: t('pages.members'),
                text: t('Members.findMembers'),
                isDark: true,
            }}
            topActionsMap={topActionsMap}
            configActions={config.page.actions}
        >
            <>
                <Table
                    columns={columns}
                    data={data}
                    className={styles.table}
                    filterConfig={filterConfig}
                    paginator={paginator}
                    actions={
                        [
                            /*{
                            type: 'export',
                            onClick: () => console.log('export'),
                        },*/
                        ]
                    }
                    showRowsCount
                    noLeftPadding
                />
                <MergeModal
                    openModal={showMergeModal}
                    setOpenModal={setShowMergeModal}
                />
            </>
        </AppLayout>
    );
};

export default MembersPage;
